import { apiFetch } from "@/lib/apiFetch";
import { env } from "@/lib/env";
import { ApiError } from "@/types/types";
import { toast } from "react-toastify";
import { create } from "zustand";

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  added_by: string;
  added_at: number;
  has_been_contacted: boolean;
}

interface Form {
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
}

interface ClientsResponse {
  users_list: Client[];
}

interface AddClientsStore {
  isSending: boolean;
  formData: Form;
  clients: Client[];
  changeFormField: <K extends keyof Form>(key: K, value: Form[K]) => void;
  resetForm: () => void;
  addClient: (idToken: string | null) => Promise<void>;
  initClients: (idToken: string | null) => Promise<void>;
}

const ADMINENPOINTURL = env.NEXT_ADMIN_API_URL as string;

export const useAddClientsStore = create<AddClientsStore>((set, get) => ({
  isSending: false,
  formData: {
    first_name: "",
    last_name: "",
    phone_number: "",
    email: "",
  },
  changeFormField: (key, value) => {
    set((state) => ({
      formData: {
        ...state.formData,
        [key]: value,
      },
    }));
  },
  resetForm: () => {
    set({
      formData: {
        first_name: "",
        last_name: "",
        phone_number: "",
        email: "",
      },
    });
  },
  clients: [],
  addClient: async (idToken) => {
    set({ isSending: true });
    const formData = get().formData;
    try {
      const response = await apiFetch(`/api/admin/addClients`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
        cache: "no-cache",
      });
      if (response.ok) {
        const data = (await response.json()) as Client;
        set((state) => ({
          clients: [data, ...state.clients],
          isSending: false,
        }));
        get().resetForm();
        return;
      } else {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to get clients."
          : errorMessage,
        {
          type: "error",
        },
      );
      set({ isSending: false });
      return;
    }
  },
  initClients: async (idToken) => {
    try {
      const response = await apiFetch(`/api/admin/getClients`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = (await response.json()) as ClientsResponse;
        set({
          clients: data.users_list.sort((a, b) => b.added_at - a.added_at),
        });
        return;
      } else {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to get clients."
          : errorMessage,
        {
          type: "error",
        },
      );
      return;
    }
  },
}));
